import { useCallback, useMemo, useState } from 'react';

import {
  AGENT_CHAT_MAX_FILES_PER_MESSAGE,
  AGENT_CHAT_MAX_FILE_BYTES,
  formatAgentAttachmentError,
  validateAgentAttachments,
  type AgentAttachmentValidationError,
} from './agent-chat-attachments';

export type UseAgentChatAttachmentsOptions = {
  maxFiles?: number;
  maxBytes?: number;
};

type AttachmentState = {
  attachments: readonly File[];
  errors: readonly AgentAttachmentValidationError[];
};

const emptyState: AttachmentState = { attachments: [], errors: [] };

function addFilesReducer(
  prev: AttachmentState,
  incoming: File[],
  maxFiles: number,
  maxBytes: number
): AttachmentState {
  const existing = prev.attachments.slice();
  const existingKeys = new Set(existing.map(f => `${f.name}:${f.size}`));
  const dedupedIncoming = incoming.filter(f => !existingKeys.has(`${f.name}:${f.size}`));
  const { accepted, errors: validationErrors } = validateAgentAttachments(
    dedupedIncoming,
    existing.length,
    { maxFiles, maxBytes }
  );
  const nextAttachments = accepted.length > 0 ? [...existing, ...accepted] : existing;
  return {
    attachments: nextAttachments,
    errors: validationErrors.length > 0 ? validationErrors : [],
  };
}

export type UseAgentChatAttachmentsResult = {
  attachments: readonly File[];
  errors: readonly AgentAttachmentValidationError[];
  errorMessages: readonly string[];
  addFiles: (files: FileList | File[] | null | undefined) => void;
  removeAt: (index: number) => void;
  clear: () => void;
  clearErrors: () => void;
  maxFiles: number;
  maxBytes: number;
    isAtLimit: boolean;
};

/**
 * React hook that owns the pending-attachment list for a single compose action.
 * Enforces per-message file count and per-file byte limits, dedupes by
 * ``name+size``, and exposes validation errors the caller can surface in the UI.
 */
export function useAgentChatAttachments(
  options: UseAgentChatAttachmentsOptions = {}
): UseAgentChatAttachmentsResult {
  const maxFiles = options.maxFiles ?? AGENT_CHAT_MAX_FILES_PER_MESSAGE;
  const maxBytes = options.maxBytes ?? AGENT_CHAT_MAX_FILE_BYTES;

  const [state, setState] = useState<AttachmentState>(emptyState);
  const { attachments, errors } = state;

  const addFiles = useCallback(
    (files: FileList | File[] | null | undefined) => {
      if (!files) return;
      const incoming = Array.from(files as ArrayLike<File>);
      if (incoming.length === 0) return;
      setState(prev => addFilesReducer(prev, incoming, maxFiles, maxBytes));
    },
    [maxFiles, maxBytes]
  );

  const removeAt = useCallback((index: number) => {
    setState(prev => {
      if (index < 0 || index >= prev.attachments.length) return prev;
      const next = prev.attachments.slice();
      next.splice(index, 1);
      return { ...prev, attachments: next };
    });
  }, []);

  const clear = useCallback(() => {
    setState(emptyState);
  }, []);

  const clearErrors = useCallback(() => {
    setState(prev => (prev.errors.length === 0 ? prev : { ...prev, errors: [] }));
  }, []);

  const errorMessages = useMemo(() => errors.map(formatAgentAttachmentError), [errors]);

  return {
    attachments,
    errors,
    errorMessages,
    addFiles,
    removeAt,
    clear,
    clearErrors,
    maxFiles,
    maxBytes,
    isAtLimit: attachments.length >= maxFiles,
  };
}
